import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../context/HimmaContext';
import {
  AddOrEditCmsPageRequestSchema,
  DeleteSubAdminsRequest,
  ToggleSubAdminStatusRequest,
} from '../client-sdk/api';

export const useCMSApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCMS = useCallback(
    async (CMSData: AddOrEditCmsPageRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addCmsPage(CMSData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getAllCMS = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const response = await api.getAllCmsPages(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          searchParam
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getToggleStatus = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleSubAdminStatusRequest: ToggleSubAdminStatusRequest = {
          ids: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleCmsPagesStatus(
          toggleSubAdminStatusRequest
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const deleteCMS = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const deleteSubAdmins: DeleteSubAdminsRequest = {
          ids: query,
        };
        const response = await api.deleteCmsPages(deleteSubAdmins);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const editCMS = useCallback(
    async (id: any, cmsData: AddOrEditCmsPageRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editCmsPage(id, cmsData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    createCMS,
    getToggleStatus,
    getAllCMS,
    deleteCMS,
    editCMS,
    loading,
    error,
  };
};
